import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "../lib/utils";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

type MonthPickerValue = {
  month: number; // 1-based
  year: number;
};

type MonthPickerProps = {
  label?: string;
  value: MonthPickerValue;
  onChange: (value: MonthPickerValue) => void;
  className?: string;
};

export default function MonthPicker({
  label,
  value,
  onChange,
  className,
}: Readonly<MonthPickerProps>) {
  const [open, setOpen] = useState(false);
  const [yearInput, setYearInput] = useState(value.year.toString());

  const displayLabel = useMemo(() => {
    const monthName = MONTHS[value.month - 1] ?? "";
    return `${monthName} ${value.year}`;
  }, [value.month, value.year]);

  const handleMonthSelect = (monthIndex: number) => {
    const safeMonth = Math.min(Math.max(monthIndex + 1, 1), 12);
    onChange({
      month: safeMonth,
      year: Number.parseInt(yearInput, 10) || value.year,
    });
    setOpen(false);
  };

  const handleYearChange = (delta: number) => {
    const nextYear = Number.parseInt(yearInput, 10) || value.year;
    const updatedYear = nextYear + delta;
    setYearInput(updatedYear.toString());
    onChange({ month: value.month, year: updatedYear });
  };

  const handleYearInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const next = event.target.value;
    setYearInput(next);
    const parsed = Number.parseInt(next, 10);
    if (!Number.isNaN(parsed)) {
      onChange({ month: value.month, year: parsed });
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setYearInput(value.year.toString());
    }
  };

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {label ? <span className="text-xs text-white/60">{label}</span> : null}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="justify-between bg-transparent text-sm font-normal hover:bg-[#242A30] rounded-full">
            {displayLabel}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-64 bg-[#1F242A] border-white/10 text-white">
          <div className="flex items-center justify-between gap-2 mb-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleYearChange(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Input
              value={yearInput}
              onChange={handleYearInputChange}
              className="h-8 w-24 text-center bg-[#121717] border-white/10"
              inputMode="numeric"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleYearChange(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {MONTHS.map((monthName, index) => {
              const isActive = value.month === index + 1;
              return (
                <Button
                  key={monthName}
                  type="button"
                  variant={isActive ? "primary" : "outline"}
                  className={cn(
                    "h-9 text-xs",
                    isActive
                      ? "bg-primary text-black hover:bg-primary/90"
                      : "bg-transparent! border-none! text-white hover:bg-black/20! rounded-full"
                  )}
                  onClick={() => handleMonthSelect(index)}>
                  {monthName.slice(0, 3)}
                </Button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
